import React, {useMemo} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {get, useFormContext} from "react-hook-form";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheckDouble} from "@fortawesome/free-solid-svg-icons";

// Utils
import {defaultUnacceptableParameterPrompt} from "../../utils/builder.js";

// Components
import InputError from "./InputError.js";
import InputTextArea from "./InputTextArea.js";
import Help from "../Help.js";
import FieldLinkHelp from "./FieldLinkHelp.js";

// Style
import {voice} from "../../style/components/typography.js";
import {flex} from "../../style/components/mixins.js";
import {border, colors, pad, radius} from "../../style/components/variables.js";
import {Abbr, Error, FormFieldWrapper, Label, Pill} from "../../style/components/general.js";

const InputCheck = ({children, ...props}) => {
  const {
    testId = "input-checkbox",
    name,
    label,
    required = false,
    hidden = false,
    disabled = false,
    showError = true,
    minWidth,
    globalUnacceptable,
    globalUnacceptableTitles,
    hasAlert = false,
    alertMessage,
    alertCondition,
    condition,
    invertColors = false,
    tag,
    shared = false,
    linkedAcrossStates = false,
    value
  } = props;

  const {
    register,
    watch,
    formState: {errors}
  } = useFormContext();

  const selected = watch(name);

  const hasYupErrors = useMemo(() => {
    const {message} = get(errors, name) || {};
    return !!message;
  }, [errors, name]);

  return (
    <FormFieldWrapper data-testid={testId} hidden={hidden} standard>
      {label && (
        <FieldLabel htmlFor={name} bold inline>
          {globalUnacceptable}
          {label.toUpperCase()}
          {required && "*"}
          {tag && (
            <Pill quiet>
              <Abbr title={tag}>{tag}</Abbr>
            </Pill>
          )}
          <FieldLinkHelp shared={shared} linkedAcrossStates={linkedAcrossStates} />
          {condition && <Help icon={<FontAwesomeIcon icon={faCheckDouble} />}>{condition}</Help>}
        </FieldLabel>
      )}
      <CheckLabel>
        <CheckWrapper>
          <CheckInput
            type="checkbox"
            id={name + value}
            value={value}
            disabled={disabled}
            checked={selected === value || (Array.isArray(selected) && selected.includes(value))}
            data-testid={`${testId}-input`}
            {...register(name, {required})}
          />
          <CheckMark />
        </CheckWrapper>
        {children && (
          <Description minWidth={minWidth} inverted={invertColors}>
            {children}
          </Description>
        )}
      </CheckLabel>

      {showError && errors && (
        <>
          <InputError errors={errors} name={name} />
          {!hasYupErrors && globalUnacceptableTitles?.length && (
            <Error>
              Parameter violation{globalUnacceptableTitles.length > 1 ? "s" : ""}:{" "}
              {globalUnacceptableTitles.join(", ")}
            </Error>
          )}
        </>
      )}

      {hasAlert &&
        (selected === alertCondition ||
          (Array.isArray(selected) && selected.includes(alertCondition))) && (
          <InputTextArea
            name={`${name}_comment`}
            label={alertMessage || defaultUnacceptableParameterPrompt}
            required
            maxLength={1000}
          />
        )}
    </FormFieldWrapper>
  );
};

InputCheck.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  children: PropTypes.node,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  showError: PropTypes.bool,
  hidden: PropTypes.bool,
  testId: PropTypes.string,
  minWidth: PropTypes.string,
  globalUnacceptable: PropTypes.node,
  globalUnacceptableTitles: PropTypes.arrayOf(PropTypes.string),
  hasAlert: PropTypes.bool,
  alertMessage: PropTypes.string,
  alertCondition: PropTypes.any,
  condition: PropTypes.string,
  invertColors: PropTypes.bool,
  tag: PropTypes.string,
  shared: PropTypes.bool,
  linkedAcrossStates: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.any)])
};

// Style Overrides
const FieldLabel = styled(Label)`
  white-space: normal;
  gap: ${pad / 2}px;
  margin-bottom: 2px;
`;

const CheckLabel = styled(Label)`
  ${flex("row", "nowrap", "start", "start")};
  gap: ${pad / 2}px;
`;

const Description = styled.span`
  text-align: left;
  color: ${({theme}) => theme.secondary};
  white-space: break-spaces;

  ${voice.normal};

  ${({inverted, theme}) =>
    inverted &&
    css`
      color: ${theme.tertiary};
    `};

  ${({minWidth}) =>
    minWidth
      ? css`
          min-width: ${minWidth};
        `
      : css`
          max-width: 85%;
        `}
`;

const CheckWrapper = styled.div`
  ${flex("row", "nowrap", "center", "center")};
  position: relative;
  width: 18px;
  height: 18px;
  margin-top: 2px;
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  background: ${({theme}) => theme.tertiary};

  &:hover {
    cursor: pointer;
  }

  ${({disabled}) =>
    disabled &&
    css`
      background-color: ${colors.grayLight};
      &:hover {
        cursor: not-allowed;
      }
    `}
`;

const CheckMark = styled.span`
  width: 100%;
  height: 100%;

  &:after {
    content: "";
    opacity: 0;
    position: absolute;
    top: 0;
    left: 4px;
    width: 37%;
    height: 85%;
    border: ${border} solid ${({theme}) => theme.secondary};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    transition: all ease 0.1s;
  }
`;

const CheckInput = styled.input`
  display: none;
  &:checked + span:after {
    opacity: 1;
  }
`;

export default InputCheck;
